import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Application configuration
const APPLICATIONS = {
  PART1: {
    id: "PART1",
    name: "MISSING: The Search for Terry Hughes",
    shortName: "MISSING Part I"
  },
  PART2: {
    id: "PART2",
    name: "MISSING: The Sequel",
    shortName: "MISSING Part II"
  }
};

const Registration = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [token, setToken] = useState("");
  const [userType, setUserType] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [application, setApplication] = useState("PART1"); // Default to Part I
  const [error, setError] = useState("");
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Password validation function
  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push("Password must be at least 8 characters long");
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push("Password must contain at least one uppercase letter");
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push("Password must contain at least one lowercase letter");
    }
    
    if (!/[0-9]/.test(password)) {
      errors.push("Password must contain at least one number");
    }
    
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push("Password must contain at least one special character");
    }
    
    return errors;
  };
  
  // Update password errors when password changes
  useEffect(() => {
    if (password) {
      setPasswordErrors(validatePassword(password));
    } else {
      setPasswordErrors([]);
    }
  }, [password]);
  
  // Extract query parameters on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailParam = params.get("email");
    const tokenParam = params.get("token");
    const typeParam = params.get("type");
    const buyerParam = params.get("buyer");
    const appParam = params.get("application");
    
    if (emailParam) setEmail(emailParam);
    if (tokenParam) setToken(tokenParam);
    if (typeParam) setUserType(typeParam);
    if (buyerParam) setBuyerEmail(buyerParam);
    if (appParam && APPLICATIONS[appParam]) setApplication(appParam);
    
    // Verify the invitation token
    verifyInvitation(emailParam, tokenParam, typeParam, appParam);
  }, [location]);
  
  // Verify invitation token
  const verifyInvitation = async (email, token, userType, appParam) => {
    if (!email || !token || !userType) {
      setError("Invalid registration link. Please check the link and try again.");
      setVerifying(false);
      return;
    }
    
    try {
      console.log("Verifying invitation with:", { email, token, userType, application: appParam });
      
      const response = await fetch("/verify-invitation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ 
          email, 
          token, 
          userType
        })
      });
      
      // Log the raw response for debugging
      const rawText = await response.text();
      console.log("Raw response:", rawText);
      
      // Try to parse as JSON
      let data;
      try {
        data = JSON.parse(rawText);
      } catch (parseError) {
        console.error("Error parsing response:", parseError);
        throw new Error("Server returned invalid data. Please try again later.");
      }
      
      if (!response.ok) {
        setError(data.error || "Invalid invitation.");
        setVerifying(false);
        return;
      }
      
      // If the invitation is for a User and includes a buyer, store it
      if (userType === "User" && data.buyerEmail) {
        setBuyerEmail(data.buyerEmail);
      }
      
      // Update application if returned from the verification
      if (data.application && APPLICATIONS[data.application]) {
        setApplication(data.application);
      }
      
      setVerified(true);
      setVerifying(false);
    } catch (err) {
      console.error("Error verifying invitation:", err);
      setError("Failed to verify invitation. Please try again later.");
      setVerifying(false);
    }
  };  

  // Function to handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if password meets requirements
    const errors = validatePassword(password);
    if (errors.length > 0) {
      setError("Password doesn't meet all requirements.");
      return;
    }
    
    // Check if passwords match
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      const response = await fetch("/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email,
          password,
          token,
          userType,
          buyerEmail,
          application
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || "Registration failed.");
      }
      
      // Show success message and redirect to login
      alert("Registration successful! You can now log in.");
      navigate("/");
    } catch (err) {
      console.error("Registration error:", err);
      setError(err.message || "Failed to register. Please try again later.");
    } finally {
      setLoading(false);
    }
  };
  
  // Get application name
  const getApplicationName = () => {
    return APPLICATIONS[application]?.name || "MISSING";
  };
  
  // Get application short name
  const getApplicationShortName = () => {
    return APPLICATIONS[application]?.shortName || "MISSING";
  };
  
  // Show loading state while verifying the invitation
  if (verifying) {
    return (
      <div style={{ 
        maxWidth: "500px", 
        margin: "0 auto", 
        padding: "2rem",
        textAlign: "center" 
      }}>
        <h1>Verifying Invitation...</h1>
        <p>Please wait while we verify your invitation.</p>
      </div>
    );
  }
  
  // If the invitation is invalid, show error
  if (!verified) {
    return (
      <div style={{ 
        maxWidth: "500px", 
        margin: "0 auto", 
        padding: "2rem",
        textAlign: "center",
        color: "#d32f2f"
      }}>
        <h1>Invalid Invitation</h1>
        <p>{error || "Your invitation link is invalid or has expired."}</p>
        <button
          onClick={() => navigate("/")}
          style={{ 
            padding: "0.75rem 1.5rem", 
            backgroundColor: "#2196F3", 
            color: "white", 
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            marginTop: "1rem"
          }}
        >
          Return to Login
        </button>
      </div>
    );
  }
  
  // Show registration form if the invitation is valid
  return (
    <div style={{ 
      maxWidth: "500px", 
      margin: "0 auto", 
      padding: "2rem",
      borderRadius: "8px",
      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
      backgroundColor: "white",
      marginTop: "3rem"
    }}>
      <h1 style={{ 
        fontSize: "1.5rem", 
        textAlign: "center", 
        marginBottom: "1rem",
        color: "#333"
      }}>
        Complete Your Registration
      </h1>
      
      <p style={{ 
        textAlign: "center", 
        marginBottom: "0.5rem",
        color: "#666"
      }}>
        You've been invited to join as a{userType === "Buyer" ? " Buyer" : " User"}.
        {userType === "User" && buyerEmail && ` Invited by: ${buyerEmail}`}
      </p>
      
      <p style={{ 
        textAlign: "center", 
        marginBottom: "2rem",
        color: "#666",
        fontWeight: "bold"
      }}>
        Application: {getApplicationName()}
      </p>
      
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: "1.5rem" }}>
          <label style={{ 
            display: "block", 
            marginBottom: "0.5rem", 
            fontWeight: "500", 
            color: "#555"
          }}>
            Email Address
          </label>
          <input
            type="email"
            value={email}
            readOnly
            style={{ 
              width: "100%", 
              padding: "0.75rem",
              borderRadius: "4px",
              border: "1px solid #ddd",
              backgroundColor: "#f5f5f5",
              fontSize: "1rem"
            }}
          />
        </div>
        
        <div style={{ marginBottom: "1.5rem" }}>
          <label style={{ 
            display: "block", 
            marginBottom: "0.5rem", 
            fontWeight: "500", 
            color: "#555"
          }}>
            Create Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={{ 
              width: "100%", 
              padding: "0.75rem",
              borderRadius: "4px",
              border: `1px solid ${passwordErrors.length > 0 && password ? "#d32f2f" : "#ddd"}`,
              fontSize: "1rem"
            }}
            placeholder="Enter a secure password"
          />
          
          {password && (
            <div style={{ 
              marginTop: "0.5rem", 
              fontSize: "0.9rem", 
              color: "#666",
              backgroundColor: "#f5f5f5",
              padding: "1rem",
              borderRadius: "4px" 
            }}>
              <p style={{ marginTop: 0, fontWeight: "500" }}>Password requirements:</p>
              <ul style={{ paddingLeft: "1.5rem", marginBottom: 0 }}>
                <li style={{ 
                  color: password.length >= 8 ? "#4CAF50" : "#d32f2f",
                  marginBottom: "0.25rem"
                }}>
                  At least 8 characters
                </li>
                <li style={{ 
                  color: /[A-Z]/.test(password) ? "#4CAF50" : "#d32f2f",
                  marginBottom: "0.25rem" 
                }}>
                  At least one uppercase letter
                </li>
                <li style={{ 
                  color: /[a-z]/.test(password) ? "#4CAF50" : "#d32f2f",
                  marginBottom: "0.25rem" 
                }}>
                  At least one lowercase letter
                </li>
                <li style={{ 
                  color: /[0-9]/.test(password) ? "#4CAF50" : "#d32f2f",
                  marginBottom: "0.25rem" 
                }}>
                  At least one number
                </li>
                <li style={{ 
                  color: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>\/?]/.test(password) ? "#4CAF50" : "#d32f2f"
                }}>
                  At least one special character
                </li>
              </ul>
            </div>
          )}
        </div>
        
        <div style={{ marginBottom: "1.5rem" }}>
          <label style={{ 
            display: "block", 
            marginBottom: "0.5rem", 
            fontWeight: "500", 
            color: "#555"
          }}>
            Confirm Password
          </label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            style={{ 
              width: "100%", 
              padding: "0.75rem",
              borderRadius: "4px",
              border: `1px solid ${password !== confirmPassword && confirmPassword ? "#d32f2f" : "#ddd"}`,
              fontSize: "1rem"
            }}
            placeholder="Confirm your password"
          />
          
          {password !== confirmPassword && confirmPassword && (
            <p style={{ 
              color: "#d32f2f", 
              marginTop: "0.5rem", 
              fontSize: "0.9rem" 
            }}>
              Passwords do not match
            </p>
          )}
        </div>
        
        {error && (
          <div style={{ 
            color: "#d32f2f", 
            backgroundColor: "#ffebee", 
            padding: "0.75rem", 
            borderRadius: "4px",
            marginBottom: "1.5rem"
          }}>
            {error}
          </div>
        )}
        
        <button 
          type="submit" 
          disabled={loading || passwordErrors.length > 0 || password !== confirmPassword}
          style={{ 
            width: "100%", 
            padding: "0.75rem", 
            backgroundColor: "#4CAF50", 
            color: "white", 
            border: "none",
            borderRadius: "4px",
            fontSize: "1rem",
            fontWeight: "500",
            cursor: loading || passwordErrors.length > 0 || password !== confirmPassword ? "default" : "pointer",
            opacity: loading || passwordErrors.length > 0 || password !== confirmPassword ? 0.7 : 1
          }}
        >
          {loading ? "Registering..." : "Complete Registration"}
        </button>
      </form>
      
      <p style={{ 
        marginTop: "1.5rem", 
        textAlign: "center",
        fontSize: "0.9rem",
        color: "#666"
      }}>
        Already have an account?{" "}
        <a 
          href="/" 
          style={{ 
            color: "#2196F3", 
            textDecoration: "none" 
          }}
        >
          Sign in
        </a>
      </p>
    </div>
  );
};

export default Registration;